import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { theme } from "../theme";
import { api } from "../api/client";
import { PlaybookCriteriaChips } from "./PlaybookCriteriaChips";
import { RecommendationTasksDialog } from "./RecommendationTasksDialog";
import { useCurrentUser } from "../auth/useCurrentUser";
import { recommendationKey, type RaisedRecommendation, type StepDetail } from "../lib/recommendationTasks";

import { cbPalette } from "../lib/colourBlind";
export interface ControlInsights {
  evidenceFound: string[];
  gaps: string[];
  recommendations: string[];
  status: "COMPLIANT" | "GAP" | "NOT_STARTED";
  /**
   * Set when the verdict turns on the organisation's own adopted policy — a
   * commitment nothing in the operational record demonstrates, or a boundary
   * the record breaches (backend/src/lib/policyAdoption.ts).
   */
  policyConflict?: string;
  /** Good-practice playbook criteria the verdict relied on, e.g. "IR-3" (backend/src/lib/playbooks.ts). */
  playbookCriteria?: string[];
}

interface Props {
  insights: ControlInsights | null;
  controlId: string;
  controlName: string;
  framework: string;
  status: string;
  notes?: string;
  guidance?: string[];
}

const COL_STYLES = cbPalette({
  evidence: { headColor: "#22c55e", headBg: "#22c55e18", bullet: "✓", bulletColor: "#22c55e" },
  gaps:     { headColor: "#ef4444", headBg: "#ef444418", bullet: "✗", bulletColor: "#ef4444" },
  actions:  { headColor: "#f59e0b", headBg: "#f59e0b18", bullet: "→", bulletColor: "#f59e0b" },
});

function ActionItem({ text, controlId, controlName, framework, status, raised, canRaise, onRaised }: {
  text: string;
  controlId: string;
  controlName: string;
  framework: string;
  status: string;
  /** The corrective action already raised from this recommendation, if any. */
  raised: RaisedRecommendation | null;
  canRaise: boolean;
  onRaised: (key: string, action: { id: string; displayId: number | null }, steps: number) => void;
}) {
  const [expanded, setExpanded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [steps, setSteps] = useState<string[] | null>(null);
  const [details, setDetails] = useState<StepDetail[] | null>(null);
  const [fetchError, setFetchError] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [openWhenLoaded, setOpenWhenLoaded] = useState(false);
  const abortRef = useRef<AbortController | null>(null);

  async function load() {
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    setLoading(true);
    setFetchError(false);
    try {
      const res = await api.postAbortable<{ steps: string[]; stepDetails?: StepDetail[] }>("/guidance/action-steps", {
        controlId, controlName, framework, status, action: text,
      }, controller.signal);
      if (!controller.signal.aborted) {
        setSteps(res.steps ?? []);
        // An API from before named people per step still gives the plain steps.
        setDetails(res.stepDetails ?? (res.steps ?? []).map((s) => ({ text: s, people: [] })));
      }
    } catch {
      if (!controller.signal.aborted) { setFetchError(true); setOpenWhenLoaded(false); }
    } finally {
      if (!controller.signal.aborted) setLoading(false);
    }
  }

  async function expand() {
    if (expanded) {
      abortRef.current?.abort();
      setExpanded(false);
      setLoading(false);
      setOpenWhenLoaded(false);
      return;
    }
    setExpanded(true);
    if (steps !== null) return; // already fetched
    await load();
  }

  // "Create tasks" needs the steps; fetch them first when the row has not been opened yet.
  function createTasks() {
    if (details && details.length) { setDialogOpen(true); return; }
    setOpenWhenLoaded(true);
    if (!expanded) void expand();
  }
  useEffect(() => {
    if (openWhenLoaded && details && details.length) { setOpenWhenLoaded(false); setDialogOpen(true); }
  }, [openWhenLoaded, details]);

  return (
    <div style={{ marginBottom: 6 }}>
      <div style={{ display: "flex", alignItems: "flex-start", gap: 6 }}>
        <span style={{ color: COL_STYLES.actions.bulletColor, fontSize: 12, marginTop: 1, flexShrink: 0 }}>→</span>
        <span style={{ fontSize: 12, color: theme.color.text, flex: 1, lineHeight: 1.5 }}>{text}</span>
        {raised ? (
          <Link
            to={`/improvement?action=${raised.id}`}
            title="This recommendation is already a corrective action"
            style={{ fontSize: 10, fontWeight: 600, color: theme.color.accentText, background: theme.color.accentBg, borderRadius: 2, padding: "2px 6px", whiteSpace: "nowrap", flexShrink: 0, textDecoration: "none" }}
          >
            CA-{raised.displayId}{raised.steps > 0 ? ` · ${raised.stepsDone}/${raised.steps} steps` : ""}
          </Link>
        ) : canRaise && (
          <button
            type="button"
            onClick={createTasks}
            disabled={loading && openWhenLoaded}
            title="Turn this recommendation into a corrective action with a task for each step"
            style={{ background: "none", border: `1px solid ${theme.color.border}`, borderRadius: 2, cursor: "pointer", fontSize: 10, fontWeight: 600, color: theme.color.accentText, padding: "1px 6px", flexShrink: 0, whiteSpace: "nowrap" }}
          >
            {loading && openWhenLoaded ? "Preparing…" : "Create tasks"}
          </button>
        )}
        <button
          onClick={expand}
          title={expanded ? "Collapse implementation steps" : "Show implementation steps"}
          style={{ background: "none", border: "none", cursor: "pointer", fontSize: 10, color: theme.color.faint, padding: "2px 4px", flexShrink: 0, marginTop: 1 }}
        >
          {expanded ? "▲" : "▼"}
        </button>
      </div>
      {expanded && (
        <div style={{ marginLeft: 18, marginTop: 6, paddingLeft: 10, borderLeft: `2px solid ${theme.color.border}` }}>
          {loading && <span style={{ fontSize: 11, color: theme.color.faint }}>Loading implementation steps…</span>}
          {fetchError && (
            <span style={{ fontSize: 11, color: theme.color.dangerText }}>
              Could not load steps.{" "}
              <button
                onClick={() => { setSteps(null); setDetails(null); setFetchError(false); void load(); }}
                style={{ background: "none", border: "none", cursor: "pointer", fontSize: 11, color: theme.color.accent, padding: 0, textDecoration: "underline" }}
              >
                Retry
              </button>
            </span>
          )}
          {steps !== null && steps.length === 0 && !fetchError && (
            <span style={{ fontSize: 11, color: theme.color.faint }}>No implementation steps returned. Try refreshing.</span>
          )}
          {steps !== null && steps.length > 0 && steps.map((step, i) => (
            <div key={i} style={{ fontSize: 11, color: theme.color.subtle, marginBottom: 5, lineHeight: 1.6 }}>
              <span style={{ fontWeight: 600, color: theme.color.muted, marginRight: 4 }}>{i + 1}.</span>
              {step}
            </div>
          ))}
        </div>
      )}
      {dialogOpen && details && (
        <RecommendationTasksDialog
          recommendation={text}
          steps={details}
          frameworkId={framework}
          controlId={controlId}
          onClose={() => setDialogOpen(false)}
          onCreated={(action, stepCount) => { setDialogOpen(false); onRaised(recommendationKey(text), action, stepCount); }}
        />
      )}
    </div>
  );
}

export function ControlInsightsPanel({ insights, controlId, controlName, framework, status, notes, guidance }: Props) {
  const { user } = useCurrentUser();
  // The API raises these as controls:write (improvement.routes.ts actionPermissions).
  const canRaise = !!user?.permissions?.includes("controls:write");
  const [raised, setRaised] = useState<Record<string, RaisedRecommendation>>({});
  const hasRecommendations = (insights?.recommendations.length ?? 0) > 0;

  useEffect(() => {
    if (!hasRecommendations) return;
    let live = true;
    api.get<{ actions: RaisedRecommendation[] }>("/improvement/recommendation-actions", { frameworkId: framework, controlId })
      .then((d) => { if (live) setRaised(Object.fromEntries(d.actions.map((a) => [a.key, a]))); })
      .catch(() => { /* a member who cannot read the register still sees the recommendations */ });
    return () => { live = false; };
  }, [framework, controlId, hasRecommendations]);

  if (!insights) return null;

  const hasContent =
    insights.evidenceFound.length > 0 ||
    insights.gaps.length > 0 ||
    insights.recommendations.length > 0 ||
    !!insights.policyConflict ||
    (insights.playbookCriteria?.length ?? 0) > 0;

  if (!hasContent) return null;

  return (
    <div style={{ marginTop: 16, borderTop: `1px solid ${theme.color.border}`, paddingTop: 14 }}>
      <div style={{ fontSize: 11, fontWeight: 700, color: theme.color.muted, textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 10 }}>
        AI Assessment
      </div>
      {insights.policyConflict && (
        // The organisation's own policy said one thing and the record shows
        // another: the finding that only the three-layer check can produce.
        <div
          style={{
            display: "flex", gap: 8, alignItems: "flex-start", marginBottom: 10,
            background: theme.color.warning + "1e", border: `1px solid ${theme.color.warning}`,
            borderRadius: 3, padding: "9px 12px",
          }}
        >
          <span style={{ fontSize: 10, fontWeight: 700, color: theme.color.warningText, textTransform: "uppercase", letterSpacing: "0.04em", whiteSpace: "nowrap", marginTop: 1 }}>
            Against your policy
          </span>
          <span style={{ fontSize: 12, color: theme.color.text, lineHeight: 1.5 }}>{insights.policyConflict}</span>
        </div>
      )}
      {insights.playbookCriteria && insights.playbookCriteria.length > 0 && (
        <PlaybookCriteriaChips ids={insights.playbookCriteria} />
      )}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10 }}>
        {/* Evidence Found */}
        <div style={{ background: COL_STYLES.evidence.headBg, borderRadius: 3, padding: "10px 12px" }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: COL_STYLES.evidence.headColor, marginBottom: 8, textTransform: "uppercase", letterSpacing: "0.04em" }}>
            Evidence Found
          </div>
          {insights.evidenceFound.length === 0
            ? <div style={{ fontSize: 12, color: theme.color.faint }}>None identified</div>
            : insights.evidenceFound.map((e, i) => (
                <div key={i} style={{ display: "flex", gap: 6, marginBottom: 5, alignItems: "flex-start" }}>
                  <span style={{ color: COL_STYLES.evidence.bulletColor, fontSize: 12, flexShrink: 0, marginTop: 1 }}>✓</span>
                  <span style={{ fontSize: 12, color: theme.color.text, lineHeight: 1.5 }}>{e}</span>
                </div>
              ))
          }
        </div>

        {/* Gaps */}
        <div style={{ background: COL_STYLES.gaps.headBg, borderRadius: 3, padding: "10px 12px" }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: COL_STYLES.gaps.headColor, marginBottom: 8, textTransform: "uppercase", letterSpacing: "0.04em" }}>
            Gaps
          </div>
          {insights.gaps.length === 0
            ? <div style={{ fontSize: 12, color: theme.color.faint }}>No gaps identified</div>
            : insights.gaps.map((g, i) => (
                <div key={i} style={{ display: "flex", gap: 6, marginBottom: 5, alignItems: "flex-start" }}>
                  <span style={{ color: COL_STYLES.gaps.bulletColor, fontSize: 12, flexShrink: 0, marginTop: 1 }}>✗</span>
                  <span style={{ fontSize: 12, color: theme.color.text, lineHeight: 1.5 }}>{g}</span>
                </div>
              ))
          }
        </div>

        {/* Recommended Actions */}
        <div style={{ background: COL_STYLES.actions.headBg, borderRadius: 3, padding: "10px 12px" }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: COL_STYLES.actions.headColor, marginBottom: 8, textTransform: "uppercase", letterSpacing: "0.04em" }}>
            Recommended Actions
          </div>
          {insights.recommendations.length === 0
            ? <div style={{ fontSize: 12, color: theme.color.faint }}>No actions needed</div>
            : insights.recommendations.map((r, i) => (
                <ActionItem
                  key={i}
                  text={r}
                  controlId={controlId}
                  controlName={controlName}
                  framework={framework}
                  status={status}
                  raised={raised[recommendationKey(r)] ?? null}
                  canRaise={canRaise}
                  onRaised={(key, action, steps) => setRaised((m) => ({ ...m, [key]: { key, id: action.id, displayId: action.displayId, status: "proposed", steps, stepsDone: 0 } }))}
                />
              ))
          }
        </div>
      </div>
    </div>
  );
}

/** Key for a control's verdict: standard + control id, so two standards' ids never meet. */
export function insightsKey(frameworkId: string, controlId: string): string {
  return `${frameworkId}:${controlId}`;
}

// Seeded prefixes a legacy (untagged) verdict may carry. ISO 27001's own ids are unprefixed.
const LEGACY_PREFIXES = ["iso27001-", "iso42001-", "iso9001-", "soc2-"];

/**
 * Builds a lookup map from a compliance check resultJson. Each verdict is stored under
 * insightsKey(frameworkId, controlId) (the check tags every verdict with its standard; an
 * older untagged one is attributed by its seeded prefix) and under its raw id, which is a
 * unique seeded id for the pages keyed by it (Cyber Essentials keys, legal obligations).
 * Ids are never stripped of their prefix: "iso42001-6.1" would otherwise land on ISO 27001's 6.1.
 */
export function buildInsightsMap(resultJson: unknown): Map<string, ControlInsights> {
  const map = new Map<string, ControlInsights>();
  if (!resultJson || typeof resultJson !== "object") return map;
  const result = resultJson as { perControl?: Array<{ controlId: string; frameworkId?: string; status: string; evidenceFound?: string[]; gaps?: string[]; recommendations?: string[]; policyConflict?: string; playbookCriteria?: string[] }> };
  if (!Array.isArray(result.perControl)) return map;

  for (const ctrl of result.perControl) {
    const insights: ControlInsights = {
      status: ctrl.status as ControlInsights["status"],
      evidenceFound: ctrl.evidenceFound ?? [],
      gaps: ctrl.gaps ?? [],
      recommendations: ctrl.recommendations ?? [],
      ...(ctrl.policyConflict ? { policyConflict: ctrl.policyConflict } : {}),
      ...(Array.isArray(ctrl.playbookCriteria) && ctrl.playbookCriteria.length > 0 ? { playbookCriteria: ctrl.playbookCriteria } : {}),
    };
    map.set(ctrl.controlId, insights);
    const prefix = LEGACY_PREFIXES.find((p) => ctrl.controlId.startsWith(p));
    const frameworkId = ctrl.frameworkId ?? (prefix ? prefix.slice(0, -1) : null);
    if (!frameworkId) continue;
    // ISO 27001's seeded ids carry no prefix, so a legacy "iso27001-5.1" is its 5.1.
    const controlId = frameworkId === "iso27001" && ctrl.controlId.startsWith("iso27001-")
      ? ctrl.controlId.slice("iso27001-".length)
      : ctrl.controlId;
    map.set(insightsKey(frameworkId, controlId), insights);
  }
  return map;
}
